import { EPDProductClassificationId, ISO_21930_ID, YES_NO } from 'components/v2/compiler/constants';
import DangerMaterialsGridPdf from 'components/v2/compiler/grids/product-content/DangerMaterialsGridPdf';
import PackagingMaterialsGridPdf from 'components/v2/compiler/grids/product-content/PackagingMaterialsGridPdf';
import PanelContentGridPdf from 'components/v2/compiler/grids/product-content/PanelContentGridPdf';
import ProductComponentsGridPdf from 'components/v2/compiler/grids/product-content/ProductComponentsGridPdf';
import React from 'react';
import { EPDContentDeclarationProductType } from 'types/content-declaration/EPDContentDeclarationProductType';
import OtherDangerMaterialsGridPdf from 'components/v2/compiler/grids/product-content/OtherDangerMaterialsGridPdf';
import { PdfGridsArea, PdfH1, PdfH2, PdfP, PdfPage, PdfTable, PdfTdLabel } from './Styles.styled';
import usePdfData from './utils/usePdfData';
import { EPDDeclareContentProductModel } from 'types/content-declaration/EPDDeclareContentProductModel';
import { IsISO21930StandardCompliant, IsPanelContentEnabled } from 'components/v2/compiler/utils';

const ContentDeclarationPage: React.FC = () => {
  const { pcrInformation, epdDefinition, contentDeclaration } = usePdfData();
  const isPanelContentEnabled = IsPanelContentEnabled(pcrInformation);
  const isISO21930StandardSelected = IsISO21930StandardCompliant(epdDefinition);
  const averageContentDeclaration = contentDeclaration.find((x) => x.type === EPDContentDeclarationProductType.Average);
  const bestProductContentDeclaration =
    contentDeclaration.find((x) => x.type === EPDContentDeclarationProductType.BestProduct) ?? null;
  const worstProductContentDeclaration =
    contentDeclaration.find((x) => x.type === EPDContentDeclarationProductType.WorstProduct) ?? null;
  const isBestCaseEnabled = !!bestProductContentDeclaration?.productDescription;
  const isWorstCaseEnabled = !!worstProductContentDeclaration?.productDescription;
  const getHasDangerMaterialsFlagValue = (contentDeclaration: EPDDeclareContentProductModel | undefined | null) => {
    return contentDeclaration?.hasDangerMaterials ?? null;
  };
  const isAverageProductWithDangerMaterials = getHasDangerMaterialsFlagValue(
    contentDeclaration.find((x) => x.type === EPDContentDeclarationProductType.Average)
  );
  const isBestProductWithDangerMaterials = getHasDangerMaterialsFlagValue(bestProductContentDeclaration);
  const isWorstProductWithDangerMaterials = getHasDangerMaterialsFlagValue(worstProductContentDeclaration);

  return (
    <>
      <PdfPage>
        <PdfH1>Content declaration</PdfH1>
        {epdDefinition.serviceIncludesProductInSystemBoundary === YES_NO.NO && (
          <>
            <PdfH2>Explanation why content declaration is not relevant</PdfH2>
            <PdfP>{epdDefinition.declarationIrrelevanceExplanation}</PdfP>
          </>
        )}

        <PdfTable>
          <tbody>
            {epdDefinition.epdClassification &&
              epdDefinition.epdClassification !== EPDProductClassificationId.SINGLE_PRODUCT_ID && (
                <tr>
                  <PdfTdLabel>Content declaration of multiple products</PdfTdLabel>
                  <td>{averageContentDeclaration?.productDescription}</td>
                </tr>
              )}
            <tr>
              <td>Hazardous and toxic substances</td>
              <td>
                {isAverageProductWithDangerMaterials === true
                  ? 'The product contains substances from the SVHC candidate list in concentrations exceeding 0.1% of its weight.'
                  : isAverageProductWithDangerMaterials === false
                  ? 'The product does not contain any substances from the SVHC candidate list in concentrations exceeding 0.1% of its weight.'
                  : ''}
              </td>
            </tr>
          </tbody>
        </PdfTable>
        <br />

        <PdfGridsArea>
          <ProductComponentsGridPdf
            epdVersionId={epdDefinition.epdVersionId}
            serviceIncludesProductInSystemBoundary={epdDefinition.serviceIncludesProductInSystemBoundary}
            productType={epdDefinition.productType}
            contentDeclarationType={EPDContentDeclarationProductType.Average}
            isISO21930StandardSelected={isISO21930StandardSelected}
          />
          <PackagingMaterialsGridPdf
            epdVersionId={epdDefinition.epdVersionId}
            contentDeclarationType={EPDContentDeclarationProductType.Average}
            isISO21930StandardSelected={isISO21930StandardSelected}
            packagingMaterialApplicable={epdDefinition.packagingMaterialApplicable}
          />
          {isPanelContentEnabled && (
            <PanelContentGridPdf
              epdVersionId={epdDefinition.epdVersionId}
              contentDeclarationType={EPDContentDeclarationProductType.Average}
              isISO21930StandardSelected={isISO21930StandardSelected}
            />
          )}
          <DangerMaterialsGridPdf
            epdVersionId={epdDefinition.epdVersionId}
            contentDeclarationType={EPDContentDeclarationProductType.Average}
            hasDangerMaterials={averageContentDeclaration?.hasDangerMaterials}
          />
          <OtherDangerMaterialsGridPdf
            epdVersionId={epdDefinition.epdVersionId}
            contentDeclarationType={EPDContentDeclarationProductType.Average}
          />
        </PdfGridsArea>
      </PdfPage>

      {isBestCaseEnabled && (
        <PdfPage>
          <br />
          <PdfH1>Best-case product</PdfH1>
          <PdfTable>
            <tbody>
              <tr>
                <PdfTdLabel>Product name</PdfTdLabel>
                <td>{bestProductContentDeclaration?.productDescription}</td>
              </tr>
              {isBestProductWithDangerMaterials !== null && (
                <tr>
                  <td>Hazardous and toxic substances</td>
                  <td>
                    {isBestProductWithDangerMaterials === true
                      ? 'The product contains substances from the SVHC candidate list in concentrations exceeding 0.1% of its weight.'
                      : 'The product does not contain any substances from the SVHC candidate list in concentrations exceeding 0.1% of its weight.'}
                  </td>
                </tr>
              )}
            </tbody>
          </PdfTable>
          <br />

          <PdfGridsArea>
            <ProductComponentsGridPdf
              epdVersionId={epdDefinition.epdVersionId}
              serviceIncludesProductInSystemBoundary={epdDefinition.serviceIncludesProductInSystemBoundary}
              contentDeclarationType={EPDContentDeclarationProductType.BestProduct}
              isISO21930StandardSelected={isISO21930StandardSelected}
            />
            <PackagingMaterialsGridPdf
              epdVersionId={epdDefinition.epdVersionId}
              contentDeclarationType={EPDContentDeclarationProductType.BestProduct}
              isISO21930StandardSelected={isISO21930StandardSelected}
            />
            {isPanelContentEnabled && (
              <PanelContentGridPdf
                epdVersionId={epdDefinition.epdVersionId}
                contentDeclarationType={EPDContentDeclarationProductType.BestProduct}
                isISO21930StandardSelected={isISO21930StandardSelected}
              />
            )}
            <DangerMaterialsGridPdf
              epdVersionId={epdDefinition.epdVersionId}
              contentDeclarationType={EPDContentDeclarationProductType.BestProduct}
              hasDangerMaterials={bestProductContentDeclaration.hasDangerMaterials}
            />
            <OtherDangerMaterialsGridPdf
              epdVersionId={epdDefinition.epdVersionId}
              contentDeclarationType={EPDContentDeclarationProductType.BestProduct}
            />
          </PdfGridsArea>
        </PdfPage>
      )}

      {isWorstCaseEnabled && (
        <PdfPage>
          <PdfH1>Worst-case product</PdfH1>
          <PdfTable>
            <tbody>
              <tr>
                <PdfTdLabel>Product name</PdfTdLabel>
                <td>{worstProductContentDeclaration?.productDescription}</td>
              </tr>
              {isWorstProductWithDangerMaterials !== null && (
                <tr>
                  <td>Hazardous and toxic substances</td>
                  <td>
                    {isWorstProductWithDangerMaterials === true
                      ? 'The product contains substances from the SVHC candidate list in concentrations exceeding 0.1% of its weight.'
                      : 'The product does not contain any substances from the SVHC candidate list in concentrations exceeding 0.1% of its weight.'}
                  </td>
                </tr>
              )}
            </tbody>
          </PdfTable>
          <br />

          <PdfGridsArea>
            <ProductComponentsGridPdf
              epdVersionId={epdDefinition.epdVersionId}
              serviceIncludesProductInSystemBoundary={epdDefinition.serviceIncludesProductInSystemBoundary}
              contentDeclarationType={EPDContentDeclarationProductType.WorstProduct}
              isISO21930StandardSelected={isISO21930StandardSelected}
            />
            <PackagingMaterialsGridPdf
              epdVersionId={epdDefinition.epdVersionId}
              contentDeclarationType={EPDContentDeclarationProductType.WorstProduct}
              isISO21930StandardSelected={isISO21930StandardSelected}
            />
            {isPanelContentEnabled && (
              <PanelContentGridPdf
                epdVersionId={epdDefinition.epdVersionId}
                contentDeclarationType={EPDContentDeclarationProductType.WorstProduct}
                isISO21930StandardSelected={isISO21930StandardSelected}
              />
            )}
            <DangerMaterialsGridPdf
              epdVersionId={epdDefinition.epdVersionId}
              contentDeclarationType={EPDContentDeclarationProductType.WorstProduct}
              hasDangerMaterials={worstProductContentDeclaration.hasDangerMaterials}
            />
            <OtherDangerMaterialsGridPdf
              epdVersionId={epdDefinition.epdVersionId}
              contentDeclarationType={EPDContentDeclarationProductType.WorstProduct}
            />
          </PdfGridsArea>
        </PdfPage>
      )}
    </>
  );
};

export default ContentDeclarationPage;
